import { useState } from "react";
import { Plus, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/v2/Toast";
import { useInviteTeammate, type InviteRole } from "@/lib/invitations";
import { usePlanUsage, planDisplayName } from "@/lib/plan-usage";
import { useRegisterStep } from "./OnboardingShell";
import { StepShell, StepBody } from "./_StepShell";
import { StepHero } from "./_StepHero";
import { StepFooter } from "./_StepFooter";

/**
 * Step 3 — Invite team. Each row is an email + role pair. On Continue,
 * every row with a non-empty email is POSTed to /tenants/me/invitations
 * sequentially. Failures per row surface inline; Continue still advances
 * even if some failed (operator can re-send from Workspace → Team).
 *
 * Skip is allowed at any time — no rows submitted.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Row = { id: string; email: string; role: InviteRole; error?: string };

const newRow = (): Row => ({
  id: crypto.randomUUID(),
  email: "",
  role: "MEMBER",
});

export function InviteStep() {
  const invite = useInviteTeammate();
  const toast = useToast();
  const usage = usePlanUsage();
  const [rows, setRows] = useState<Row[]>([newRow()]);

  // Seat math: existing seats already on the books + the rows we
  // intend to send. Rows with non-empty emails count as "about to
  // be invited" for the purposes of disabling "Add another".
  const cap = usage?.limits.maxOperators ?? null;
  const currentSeats = usage?.current.operators ?? 0;
  const draftedSeats = rows.filter((r) => r.email.trim().length > 0).length;
  const projected = currentSeats + draftedSeats;
  const atCap = cap != null && projected >= cap;
  const planName = planDisplayName(usage?.plan ?? "");

  useRegisterStep({
    onContinue: async () => {
      const validRows = rows.filter((r) => EMAIL_RE.test(r.email.trim()));
      if (validRows.length === 0) return; // nothing to send → behave like Skip
      const results = await Promise.allSettled(
        validRows.map((r) =>
          invite.mutateAsync({
            email: r.email.trim().toLowerCase(),
            role: r.role,
          }),
        ),
      );
      const failedIndices: number[] = [];
      results.forEach((r, i) => {
        if (r.status === "rejected") failedIndices.push(i);
      });
      if (failedIndices.length === 0) {
        toast.success(
          `Invited ${validRows.length} teammate${validRows.length === 1 ? "" : "s"}`,
          "They'll get an email with a link to set their password.",
        );
      } else {
        // If any failure was a seat-cap 403, surface that with a
        // dedicated message so the operator knows to upgrade rather
        // than just retrying.
        const reasons = failedIndices.map(
          (i) => (results[i] as PromiseRejectedResult).reason as Error,
        );
        const capHit = reasons.some((r) =>
          r.message.includes("plan_operator_limit_exceeded"),
        );
        setRows((prev) =>
          prev.map((row) => {
            const failedRow = validRows[failedIndices[0]];
            if (row.id === failedRow?.id) {
              const msg = capHit
                ? "Seat cap reached — upgrade in Workspace → Billing."
                : reasons[0].message;
              return { ...row, error: msg };
            }
            return row;
          }),
        );
        if (capHit) {
          toast.error(
            "You're at your seat cap",
            "Upgrade in Workspace → Billing for more operators.",
          );
        } else {
          toast.warning(
            `${failedIndices.length} invitation${failedIndices.length === 1 ? "" : "s"} failed`,
            "Continuing — you can re-send these from Workspace → Team.",
          );
        }
      }
    },
  });

  const update = (id: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const remove = (id: string) => {
    setRows((prev) => (prev.length === 1 ? [newRow()] : prev.filter((r) => r.id !== id)));
  };
  const add = () => setRows((prev) => [...prev, newRow()]);

  return (
    <StepShell variant="form">
      <StepHero
        eyebrow="Step 3 of 5"
        title="Invite your team"
        subtitle="Each teammate gets their own login. You can always do this later from Workspace → Team."
      />

      <StepBody>
        {cap != null && (
          <div
            className={`flex items-center justify-between rounded-md border px-3.5 py-2.5 text-[12px] ${
              atCap
                ? "border-destructive/30 bg-destructive/5 text-destructive"
                : "border-border bg-muted/30 text-muted-foreground"
            }`}
          >
            <span>
              <strong className="font-semibold">{projected}</strong> of{" "}
              <strong className="font-semibold">{cap}</strong> seats used on{" "}
              {planName}
            </span>
            {atCap && (
              <span className="text-[11px]">
                Upgrade in Workspace → Billing for more.
              </span>
            )}
          </div>
        )}

        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div key={row.id} className="space-y-1">
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-1.5">
                  {idx === 0 && (
                    <Label className="text-[13px] font-medium">Email</Label>
                  )}
                  <Input
                    type="email"
                    placeholder="teammate@example.com"
                    value={row.email}
                    onChange={(e) =>
                      update(row.id, { email: e.target.value, error: undefined })
                    }
                    className={`h-11 ${row.error ? "border-destructive" : ""}`}
                  />
                </div>
                <div className="w-[140px] space-y-1.5">
                  {idx === 0 && (
                    <Label className="text-[13px] font-medium">Role</Label>
                  )}
                  <Select
                    value={row.role}
                    onValueChange={(v) =>
                      update(row.id, { role: v as InviteRole })
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MEMBER">Member</SelectItem>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={idx === 0 ? "pt-[26px]" : ""}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(row.id)}
                    aria-label="Remove row"
                    className="h-11 w-11 p-0 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {row.error && (
                <p className="text-xs text-destructive ml-1">{row.error}</p>
              )}
            </div>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={add}
            disabled={atCap}
            title={atCap ? `${planName} is limited to ${cap} operators` : undefined}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add another
          </Button>
        </div>

        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-[12px] text-muted-foreground flex items-start gap-2.5">
          <UserPlus className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            <strong className="text-foreground font-medium">Admin</strong>{" "}
            can change workspace settings + billing.{" "}
            <strong className="text-foreground font-medium">Member</strong>{" "}
            can manage bookings, clients, and inventory.
          </p>
        </div>
      </StepBody>

      <StepFooter />
    </StepShell>
  );
}
